import React, { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { useContent } from "@/context/ContentContext";
import { MapPin, Phone, Clock, MessageCircle } from "lucide-react";

export default function Contact() {
    const c = useContent();
    const cp = c.contact_page || {};
    const g = c.global || {};
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await client.post("/contact", form);
            toast.success("Thanks! We'll get back to you shortly.");
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error("Could not send message. Please try WhatsApp or phone.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="container-wide py-14">
            <p className="font-script text-brand-gold text-3xl">{cp.script}</p>
            <h1 className="font-display text-4xl md:text-5xl mt-1">{cp.title}</h1>
            <p className="mt-3 text-brand-mute max-w-2xl">
                {cp.subtitle}
            </p>

            <div className="mt-12 grid md:grid-cols-2 gap-12">
                <div className="space-y-6">
                    <div className="border border-brand-line p-6 rounded-sm">
                        <div className="flex items-start gap-3">
                            <MapPin className="text-brand-olive mt-1" size={18} />
                            <div>
                                <p className="text-xs tracking-[0.2em] text-brand-gold uppercase">Address</p>
                                <p className="mt-1 text-brand-cream">{g.address_line1}, {g.address_line2}</p>
                            </div>
                        </div>
                    </div>
                    <div className="border border-brand-line p-6 rounded-sm">
                        <div className="flex items-start gap-3">
                            <Phone className="text-brand-olive mt-1" size={18} />
                            <div>
                                <p className="text-xs tracking-[0.2em] text-brand-gold uppercase">Phone</p>
                                <a data-testid={CONTACT.call} href={`tel:${g.phone_tel}`} className="mt-1 block text-brand-cream hover:text-brand-gold">
                                    {g.phone_display}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="border border-brand-line p-6 rounded-sm">
                        <div className="flex items-start gap-3">
                            <MessageCircle className="text-brand-olive mt-1" size={18} />
                            <div>
                                <p className="text-xs tracking-[0.2em] text-brand-gold uppercase">WhatsApp</p>
                                <a data-testid={CONTACT.whatsapp} href={`https://wa.me/${g.whatsapp_number}`} target="_blank" rel="noreferrer"
                                   className="mt-1 block text-brand-cream hover:text-brand-gold">
                                    Message us on WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="border border-brand-line p-6 rounded-sm">
                        <div className="flex items-start gap-3">
                            <Clock className="text-brand-olive mt-1" size={18} />
                            <div>
                                <p className="text-xs tracking-[0.2em] text-brand-gold uppercase">Hours</p>
                                {(g.hours_lines || []).map((line, i) => (
                                    <p key={i} className="mt-1 text-brand-cream/80">{line}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="aspect-video border border-brand-line overflow-hidden">
                        <iframe
                            title="Umami Kraft Map"
                            width="100%"
                            height="100%"
                            style={{ border: 0, filter: "grayscale(0.4) contrast(1.1)" }}
                            loading="lazy"
                            src={`https://www.google.com/maps?q=${encodeURIComponent(`${g.address_line1}, ${g.address_line2}`)}&output=embed`}
                        />
                    </div>
                </div>

                <form data-testid={CONTACT.form} onSubmit={submit} className="border border-brand-line p-8 rounded-sm space-y-4 h-fit">
                    <p className="font-script text-brand-gold text-3xl leading-none">{cp.form_script || "Say hello"}</p>
                    <h2 className="font-display text-2xl">{cp.form_title || "Send an enquiry"}</h2>
                    <input
                        data-testid={CONTACT.name}
                        required
                        placeholder="Name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-brand-ink border border-brand-line px-4 py-3 rounded-sm text-brand-cream focus:outline-none focus:border-brand-olive"
                    />
                    <input
                        data-testid={CONTACT.email}
                        required
                        type="email"
                        placeholder="Email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full bg-brand-ink border border-brand-line px-4 py-3 rounded-sm text-brand-cream focus:outline-none focus:border-brand-olive"
                    />
                    <input
                        data-testid={CONTACT.phone}
                        placeholder="Phone (optional)"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full bg-brand-ink border border-brand-line px-4 py-3 rounded-sm text-brand-cream focus:outline-none focus:border-brand-olive"
                    />
                    <textarea
                        data-testid={CONTACT.message}
                        required
                        rows={5}
                        placeholder="Your message — catering enquiry, general question, feedback…"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full bg-brand-ink border border-brand-line px-4 py-3 rounded-sm text-brand-cream focus:outline-none focus:border-brand-olive resize-none"
                    />
                    <button data-testid={CONTACT.submit} disabled={submitting} className="btn-primary w-full">
                        {submitting ? "Sending…" : "Send Message"}
                    </button>
                </form>
            </div>
        </div>
    );
}
